import { useCallback, useRef, useState } from 'react'
import { SortingState } from '@atbay/cyber-components'
import useFuzzy from '@/core/hooks/useFuzzy/useFuzzy'
import useVirtualizedRowsHeight from '@/core/hooks/useVirtualizedRowsHeight/useVirtualizedRowsHeight'
import {
    AffectedAssetRow,
    UseAffectedAssetsTableParams,
} from './AffectedAssetsTable.types'

const ROW_HEIGHT = 60
const MAX_VISIBLE_ROWS = 5
const EMPTY_STATE_HEIGHT = 140

export const useAffectedAssetsTable = <T extends AffectedAssetRow>({
    rows,
    selectedIds,
    onSelectionChange,
    disabled,
    searchable,
    searchKeys,
}: UseAffectedAssetsTableParams<T>) => {
    const [search, setSearch] = useState('')
    const [sorting, setSorting] = useState<SortingState>([])
    const wrapperRef = useRef<HTMLDivElement>(null)

    const { results: searchResults } = useFuzzy(rows, search, {
        keys: searchKeys,
    })
    const visibleRows = searchable && search ? searchResults : rows

    const isAllSelected = rows.length > 0 && selectedIds.length === rows.length

    const toggleRow = useCallback(
        (rowId: string) => {
            if (disabled) return
            onSelectionChange(
                selectedIds.includes(rowId)
                    ? selectedIds.filter((id) => id !== rowId)
                    : [...selectedIds, rowId]
            )
        },
        [disabled, selectedIds, onSelectionChange]
    )

    const toggleAll = useCallback(() => {
        if (disabled) return
        onSelectionChange(isAllSelected ? [] : rows.map(({ id }) => id))
    }, [disabled, isAllSelected, rows, onSelectionChange])

    const visibleRowCount = Math.min(visibleRows.length, MAX_VISIBLE_ROWS)
    const measuredHeight = useVirtualizedRowsHeight(
        wrapperRef,
        MAX_VISIBLE_ROWS,
        ROW_HEIGHT * Math.max(visibleRowCount, 1)
    )

    const tableHeight =
        visibleRows.length === 0 ? EMPTY_STATE_HEIGHT : measuredHeight

    return {
        wrapperRef,
        search,
        setSearch,
        sorting,
        setSorting,
        visibleRows,
        tableHeight,
        rowHeight: ROW_HEIGHT,
        isAllSelected,
        toggleAll,
        toggleRow,
    }
}
